import { useEffect, useState } from "react";

import { DndContext, closestCenter } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faCirclePlus } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { UserCourseLesson } from "api/schemas/UserCourse";

import { IconLink } from "./IconLink";
import { OuterMargin, TopBar, TopLeft, TopRight } from "./SharedStyledComponents";
import { UserCourseExerciseComponent as UserCourseLessonItem } from "./UserCourseLesson";

export const LessonsList = ({
  lessons,
  onDeleteLesson,
  onSetLessons,
  newlyAddedLessons,
  onOpenLessonPicker,
  editable,
}: {
  lessons: UserCourseLesson[];
  onDeleteLesson: (id: number) => void;
  onSetLessons: (lessons: UserCourseLesson[]) => void;
  newlyAddedLessons: number[];
  onOpenLessonPicker: () => void;
  editable: boolean;
}) => {
  const { t } = useTranslation();
  const [dndLessons, setDndLessons] = useState<UserCourseLesson[]>(lessons);

  useEffect(() => {
    setDndLessons(lessons);
  }, [lessons]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      setDndLessons(prevLessons => {
        const oldIndex = prevLessons.findIndex(lesson => lesson.id === active.id);
        const newIndex = prevLessons.findIndex(lesson => lesson.id === over.id);
        const newLessons = arrayMove(prevLessons, oldIndex, newIndex);
        onSetLessons(newLessons);
        return newLessons;
      });
    }
  };

  return (
    <>
      <PanelBg>
        <OuterMargin>
          <TopBar>
            <TopLeft>
              <LeftSpace />
            </TopLeft>
            <TopRight>
              {editable && (
                <div id="tip_addlesson">
                  <IconLink
                    icon={faCirclePlus}
                    title={String(t("usercourse.add_lesson"))}
                    onClick={() => {
                      onOpenLessonPicker();
                    }}
                  />
                </div>
              )}
            </TopRight>
          </TopBar>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={dndLessons.map(lesson => lesson.id)}>
              <Lessons>
                {dndLessons.map(lesson => (
                  <SortableLesson
                    key={lesson.id}
                    lesson={lesson}
                    onDeleteLesson={onDeleteLesson}
                    editable={editable}
                    newlyAdded={newlyAddedLessons.includes(lesson.id)}
                  />
                ))}
              </Lessons>
            </SortableContext>
          </DndContext>
        </OuterMargin>
      </PanelBg>
    </>
  );
};

const SortableLesson = ({
  lesson,
  onDeleteLesson,
  editable,
  newlyAdded,
}: {
  lesson: UserCourseLesson;
  onDeleteLesson: (id: number) => void;
  editable: boolean;
  newlyAdded: boolean;
}) => {
  const { attributes, setNodeRef, transform, transition } = useSortable({
    id: lesson.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ItemRoot ref={setNodeRef} style={style} {...attributes}>
      <ItemWrapper $editable={editable}>
        <UserCourseLessonItem
          newlyAdded={newlyAdded}
          lesson={lesson}
          onDelete={() => {
            onDeleteLesson(lesson.id);
          }}
          editable={editable}
        />
      </ItemWrapper>
    </ItemRoot>
  );
};

const Lessons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PanelBg = styled.div`
  padding: 16px 0;
`;

// height: 120px;
const ItemWrapper = styled.div<{ $editable: boolean }>`
  &:hover {
    cursor: ${props => (props.$editable ? "ns-resize" : "inherit")};
    box-shadow: ${props => (props.$editable ? "0 2px 7px 0 rgba(0, 0, 0, 0.2)" : "inherit")};
  }
`;

const ItemRoot = styled.div`
  margin-left: 32px;
`;

const LeftSpace = styled.div`
  margin-left: 32px;
`;
